// PORTED COPY — A20 W2b. Original: TPRM7 packages/ui/src/reporter/ReportDialog.tsx
// (@vendorwatch/ui). Identical below this header EXCEPT for three substitutions,
// each of which swaps a @vendorwatch/ui import for this repo's equivalent:
//
//   `'use client'`           → dropped (Vite SPA, no RSC boundary)
//   <Modal> (A13·W5)         → <Dialog>/<DialogContent> from components/ui/dialog
//                              — the same Radix primitive underneath, which is
//                              where the focus trap, Esc and scroll-lock come
//                              from in both repos.
//   <Button> from ../core    → <Btn> from components/shared/primitives
//
// The copy, the field set, the severity vocabulary and BOTH honesty rules below
// are byte-for-byte the original. Diverge upstream first.
//
// A20 · W1/W2 — the report form.
//
// TWO HONESTY REQUIREMENTS drive this component's design, both learned from
// customer-portal/src/pages/Support.tsx, which today shows "your ticket has been
// submitted, we'll respond within 4 hours" unconditionally — including on total
// failure, against an endpoint that is mounted nowhere:
//
//   1. Never claim success that did not happen. The submit result is rendered
//      verbatim, failures included, with the report text still on screen so
//      nothing the user typed is lost.
//   2. Never overstate what was captured. If the recording was dropped for size
//      or rrweb failed to load, the dialog says so before the user sends.

import * as React from 'react';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Btn } from '@/components/shared/primitives';
import { useReporter } from './ReportProvider';
import type { ReportSeverity, SubmitResult } from './types';

const SEVERITIES: ReportSeverity[] = ['blocker', 'major', 'minor', 'question'];

const fieldLabel: React.CSSProperties = {
  display: 'block',
  fontSize: 12,
  fontWeight: 600,
  letterSpacing: 0.2,
  textTransform: 'uppercase',
  color: 'var(--text-muted)',
  marginBottom: 6,
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '9px 11px',
  borderRadius: 'var(--radius-md)',
  border: '1px solid var(--border)',
  background: 'var(--surface)',
  color: 'var(--text-primary)',
  fontFamily: 'var(--font-sans)',
  fontSize: 14,
  outline: 'none',
  boxSizing: 'border-box',
};

export function ReportDialog() {
  const { open, setOpen, submit, t, recorder, config, mode, lastCaptureDropped } = useReporter();

  const [title, setTitle] = React.useState('');
  const [description, setDescription] = React.useState('');
  const [severity, setSeverity] = React.useState<ReportSeverity>('major');
  const [reveal, setReveal] = React.useState(false);
  const [busy, setBusy] = React.useState(false);
  const [result, setResult] = React.useState<SubmitResult | null>(null);
  const [touched, setTouched] = React.useState(false);

  // Reset on close so the next report starts clean, but only once the dialog is
  // actually shut — resetting on open would wipe a draft if the user reopens.
  React.useEffect(() => {
    if (!open) {
      const id = setTimeout(() => {
        setTitle('');
        setDescription('');
        setSeverity('major');
        setBusy(false);
        setResult(null);
        setTouched(false);
      }, 200);
      return () => clearTimeout(id);
    }
    return undefined;
  }, [open]);

  // ── RE-MASK ON CLOSE ──────────────────────────────────────────────────────
  // THE DEFECT: `reveal` was not in the reset above, and nothing restarted the
  // recorder. Ticking "include visible text" and then pressing Cancel therefore
  // left capture running UNMASKED for the rest of the session — every later
  // page the user visited was recorded in the clear, and the checkbox that said
  // so was no longer on screen. Consent was collected for ONE report and then
  // silently applied to everything after it.
  //
  // Deliberately a SEPARATE effect with no 200ms delay. The delay above exists
  // to stop the form visibly emptying mid close-animation; the recorder posture
  // is invisible, and 200 more milliseconds of unmasked capture is 200 ms of
  // customer text we were never given permission to hold.
  //
  // It also fires after a SUCCESSFUL submit, which is the point: the consent
  // was spent on the report that was just filed.
  const revealRef = React.useRef(reveal);
  revealRef.current = reveal;
  const restart = recorder.restart; // stable useCallback; `recorder` itself is a fresh object each render
  React.useEffect(() => {
    if (open) return;
    if (!revealRef.current) return; // never restart (and discard the buffer) for nothing
    setReveal(false);
    restart({ masked: true });
  }, [open, restart]);

  const onToggleReveal = (next: boolean) => {
    setReveal(next);
    // Masking is fixed at record() start, so this necessarily restarts capture
    // and discards the buffer. The hint below the checkbox tells the user to
    // reproduce the problem again — pretending we could unmask retroactively
    // would be a lie.
    recorder.restart({ masked: !next });
  };

  const invalid = description.trim().length === 0;

  const onSubmit = async () => {
    setTouched(true);
    if (invalid || busy) return;
    setBusy(true);
    const res = await submit({
      title: title.trim() || description.trim().slice(0, 72),
      description: description.trim(),
      severity,
      includeVisibleText: reveal,
    });
    setResult(res);
    setBusy(false);
  };

  const bufferSeconds = Math.round((config.bufferMs ?? 30_000) / 1000);

  return (
    <Dialog open={open} onOpenChange={(o) => setOpen(o)}>
      <DialogContent style={{ maxWidth: 560, maxHeight: '90vh', overflowY: 'auto' }}>
        {/* The dialog is chrome, not content — never record it. */}
        <div className="ps-no-capture" style={{ display: 'grid', gap: 14 }}>
          <div>
            <DialogTitle style={{ fontSize: 16, fontWeight: 700, marginBottom: 4 }}>
              {mode === 'studio' ? t('studio.title') : t('dialog.title')}
            </DialogTitle>
            <DialogDescription style={{ fontSize: 12.5, color: 'var(--text-secondary)' }}>
              {mode === 'studio' ? t('studio.subtitle') : t('dialog.subtitle')}
            </DialogDescription>
          </div>

          {result?.ok ? (
            <p style={{ margin: 0, fontSize: 14, color: 'var(--text-primary)' }}>
              {result.queued ? t('result.queued') : t('result.ok')}
              {'ticketRef' in result && result.ticketRef ? (
                <span style={{ color: 'var(--text-muted)' }}> ({result.ticketRef})</span>
              ) : null}
            </p>
          ) : (
            <>
              {result && !result.ok && (
                <div
                  role="alert"
                  style={{
                    padding: '9px 11px',
                    borderRadius: 'var(--radius-md)',
                    border: '1px solid var(--severity-critical)',
                    background: 'var(--surface-sunken)',
                    color: 'var(--text-primary)',
                    fontSize: 13,
                  }}
                >
                  {t('result.error', { error: result.error })}
                </div>
              )}

              <div>
                <label style={fieldLabel} htmlFor="ps-report-title">
                  {t('field.title.label')}
                </label>
                <input
                  id="ps-report-title"
                  style={inputStyle}
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder={t('field.title.placeholder')}
                  maxLength={160}
                />
              </div>

              <div>
                <label style={fieldLabel} htmlFor="ps-report-desc">
                  {t('field.description.label')}
                </label>
                <textarea
                  id="ps-report-desc"
                  style={{ ...inputStyle, minHeight: 96, resize: 'vertical' }}
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder={t('field.description.placeholder')}
                  maxLength={5000}
                  aria-invalid={touched && invalid}
                />
                {touched && invalid && (
                  <p style={{ margin: '6px 0 0', fontSize: 12.5, color: 'var(--severity-critical)' }}>
                    {t('error.required')}
                  </p>
                )}
              </div>

              <div>
                <span style={fieldLabel}>{t('field.severity.label')}</span>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {SEVERITIES.map((s) => {
                    const active = severity === s;
                    return (
                      <button
                        key={s}
                        type="button"
                        onClick={() => setSeverity(s)}
                        aria-pressed={active}
                        style={{
                          padding: '6px 11px',
                          borderRadius: 999,
                          cursor: 'pointer',
                          fontFamily: 'var(--font-sans)',
                          fontSize: 13,
                          fontWeight: active ? 600 : 500,
                          border: `1px solid ${active ? 'var(--accent)' : 'var(--border)'}`,
                          background: active ? 'var(--accent)' : 'var(--surface)',
                          color: active ? '#fff' : 'var(--text-secondary)',
                        }}
                      >
                        {t(`severity.${s}` as never)}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Capture status — always stated, never implied. */}
              <div
                style={{
                  padding: '10px 12px',
                  borderRadius: 'var(--radius-md)',
                  background: 'var(--surface-sunken)',
                  border: '1px solid var(--border)',
                }}
              >
                <span style={{ ...fieldLabel, marginBottom: 4 }}>{t('capture.heading')}</span>
                <p style={{ margin: 0, fontSize: 13, color: 'var(--text-secondary)' }}>
                  {config.captureDisabled || recorder.error || !recorder.active
                    ? t('capture.off')
                    : t('capture.on', { seconds: bufferSeconds })}
                </p>
                {recorder.active && (
                  <p style={{ margin: '4px 0 0', fontSize: 12.5, color: 'var(--text-muted)' }}>
                    {reveal ? t('capture.revealed') : t('capture.masked')}
                  </p>
                )}
                {lastCaptureDropped && (
                  <p style={{ margin: '4px 0 0', fontSize: 12.5, color: 'var(--text-muted)' }}>
                    {t('capture.off')}
                  </p>
                )}

                <label
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 8,
                    marginTop: 10,
                    cursor: 'pointer',
                  }}
                >
                  <input
                    type="checkbox"
                    checked={reveal}
                    onChange={(e) => onToggleReveal(e.target.checked)}
                    style={{ marginTop: 2 }}
                  />
                  <span>
                    <span style={{ fontSize: 13, color: 'var(--text-primary)' }}>
                      {t('reveal.label')}
                    </span>
                    <span
                      style={{ display: 'block', fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}
                    >
                      {t('reveal.hint')}
                    </span>
                  </span>
                </label>
              </div>

              <p style={{ margin: 0, fontSize: 12, color: 'var(--text-muted)' }}>
                {t('privacy.note')}
              </p>
            </>
          )}

          {result?.ok ? (
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Btn kind="primary" onClick={() => setOpen(false)}>
                {t('action.close')}
              </Btn>
            </div>
          ) : (
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <Btn kind="secondary" onClick={() => setOpen(false)} disabled={busy}>
                {t('action.cancel')}
              </Btn>
              <Btn kind="primary" onClick={onSubmit} disabled={busy}>
                {busy ? t('action.submitting') : t('action.submit')}
              </Btn>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
